import { useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { AccountsFields, EMPTY_ACCOUNT } from "./AccountsFields";
import { portfolioFormSchema, toAccountInputs, type PortfolioFormValues } from "@/lib/portfolio-schema";

export function CreatePortfolio({ onCreated }: { onCreated: (slug: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<PortfolioFormValues>({
    resolver: zodResolver(portfolioFormSchema) as unknown as Resolver<PortfolioFormValues>,
    defaultValues: { accounts: [{ ...EMPTY_ACCOUNT }] },
    mode: "onTouched",
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitting(true);
    setServerError(null);
    try {
      const res = await fetch("/api/portfolios", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accounts: toAccountInputs(values.accounts) }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error ?? `HTTP ${res.status}`);
      onCreated(body.slug as string);
    } catch (err) {
      setServerError((err as Error).message);
      setSubmitting(false);
    }
  });

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-2xl flex-col justify-center p-4 md:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="font-mono">TERRNT B</span>
          <span>·</span>
          <span>Terranet B · First North Stockholm</span>
        </div>
        <h1 className="mt-1 text-3xl font-bold">Skapa din portfölj</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Lägg till dina konton med GAV och antal aktier. Du får en unik länk till din dashboard.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Konton</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-5">
              <AccountsFields />
              {serverError && <p className="text-sm text-destructive">{serverError}</p>}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Skapar…" : "Skapa portfölj"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <p className="mt-4 text-center text-xs text-muted-foreground">
        Ej finansiell rådgivning. Spara din länk — den är nyckeln till portföljen.
      </p>
    </div>
  );
}
